import React, { useState, useEffect, useMemo } from 'react';
import { FaFileDownload, FaGithub, FaArrowDown, FaJs, FaGitAlt, FaDocker, FaAws, FaNpm, FaLinkedin, FaInstagram } from 'react-icons/fa';
import { SiReact, SiNodedotjs, SiMongodb, SiTypescript, SiTailwindcss, SiExpress, SiSocketdotio, SiRedis, SiNextdotjs, SiFirebase, SiGraphql } from 'react-icons/si';
import { useInView } from '../hooks/useInView';
import { motion } from 'framer-motion';
import { fadeIn, fadeInUp } from '../utils/animations';
import { useTranslation } from 'react-i18next';

const Home: React.FC = () => {
    const [text, setText] = useState('');
    const [index, setIndex] = useState(0);
    const { ref, isInView } = useInView(0.1);

    const { t, i18n } = useTranslation();

    const phrases = useMemo(() => [
        t('home.roles.mern'),
        t('home.roles.fullstack'),
        t('home.roles.react'),
        t('home.roles.nodejs')
    ], [t]);
    
    // Reset typewriter effect when language changes
    useEffect(() => {
        setText('');
        setIndex(0);
    }, [t]);
    
    useEffect(() => {
        const currentPhrase = phrases[0];

        if (index < currentPhrase.length) {
            const timeout = setTimeout(() => {
                setText(prev => prev + currentPhrase[index]);
                setIndex(prevIndex => prevIndex + 1);
            }, 100);

            return () => clearTimeout(timeout);
        } else {
            const timeout = setTimeout(() => {
                setText('');
                setIndex(0);
            }, 3000);

            return () => clearTimeout(timeout);
        }
    }, [index, phrases]);

    const scrollToAbout = () => {
        const aboutSection = document.getElementById('about');
        if (aboutSection) {
            aboutSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <motion.section
            id="home"
            className="pt-32 pb-20 min-h-screen w-full flex items-center justify-center relative overflow-hidden"
            ref={ref as any}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeIn}
        >
            {/* Simplified background elements for better mobile performance */}
            <div className="absolute inset-0 z-0 hidden md:block">
                <div
                    className="absolute top-1/4 right-1/4 w-64 h-64 bg-primary-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-20"
                />
                <div
                    className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-accent-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-20"
                />
            </div>

            <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center mb-24 md:mb-32">
                    {/* Left content - Text and buttons */}
                    <motion.div 
                        className="lg:col-span-3 text-left lg:pr-8"
                        variants={fadeInUp}
                    >
                        <motion.h1
                            className="text-5xl md:text-7xl font-bold mb-6 font-fancy bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
                            variants={fadeInUp}
                        >
                            {t('home.title')}
                        </motion.h1>

                        <motion.div
                            className="h-16 mb-8"
                            variants={fadeInUp}
                        >
                            <div className="text-xl sm:text-2xl md:text-3xl font-medium flex flex-wrap items-center">
                                <span className="text-gray-300 mr-3">{t('home.subtitle')}</span>
                                <span className="text-primary-400 relative whitespace-nowrap">
                                    {text}
                                    <span className="absolute right-[-4px] top-0 h-full w-[2px] bg-primary-400 animate-pulse"></span>
                                </span>
                            </div>
                        </motion.div>

                        <motion.p
                            className="text-xl text-gray-300 mb-10 max-w-3xl leading-relaxed"
                            variants={fadeInUp}
                        >
                           {t('home.description')}
                        </motion.p>

                        <motion.div
                            className="flex flex-wrap gap-4 mb-12"
                            variants={fadeInUp}
                        >
                            <a
                                href="#contact"
                                onClick={(e: React.MouseEvent) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                                className="px-6 py-3 rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white font-medium text-lg hover:shadow-glow-md transition-all duration-300 flex items-center gap-2 hover:translate-y-[-8px]"
                            >
                                {t('home.buttons.getInTouch')}
                            </a>

                            <a
                                href={`/assets/resume/feresbenbrahim.${i18n.language}.pdf`}
                                download={`feresbenbrahim.${i18n.language}.pdf`}
                                className="px-6 py-3 rounded-lg glass border border-gray-700 text-white font-medium text-lg hover:border-primary-500 hover:shadow-glow-sm transition-all duration-300 flex items-center gap-2 hover:translate-y-[-8px]"
                            >
                                <FaFileDownload /> {t('home.buttons.resume')}
                            </a>
                        </motion.div>

                        <motion.div
                            className="flex space-x-6"
                            variants={fadeInUp}
                        >
                            <a
                                href="https://github.com/feresbr1"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full glass flex items-center justify-center text-gray-400 hover:text-white hover:border-primary-500 hover:shadow-glow-sm transition-all duration-300 hover:translate-y-[-8px]"
                            >
                                <FaGithub className="w-6 h-6" />
                            </a>
                            <a
                                href="https://www.linkedin.com/in/feres-ben-brahim-08a454216/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full glass flex items-center justify-center text-gray-400 hover:text-white hover:border-primary-500 hover:shadow-glow-sm transition-all duration-300 hover:translate-y-[-8px]"
                            >
                                <FaLinkedin className="w-6 h-6" />
                            </a>
                            <a
                                href="https://instagram.com/feresbr_"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full glass flex items-center justify-center text-gray-400 hover:text-white hover:border-primary-500 hover:shadow-glow-sm transition-all duration-300 hover:translate-y-[-8px]"
                            >
                                <FaInstagram className="w-6 h-6" />
                            </a>
                        </motion.div>
                    </motion.div>
                    <motion.div 
                        className="lg:col-span-2 flex justify-center lg:justify-end"
                        variants={fadeInUp}
                    >
                        <div
                            className="relative w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80"
                        >
                            <div
                                className="absolute inset-0 rounded-full bg-gradient-to-r from-primary-500/20 to-accent-500/20 backdrop-blur-sm border border-gray-700 shadow-glow-md animate-pulse-slow"
                            />
                            
                            {/* Main technologies - Fixed rotating positions */}
                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    rotate: 360
                                }}
                                transition={{ 
                                    duration: 30, 
                                    repeat: Infinity, 
                                    ease: "linear" 
                                }}
                            >
                            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-14 h-14 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-primary-500/20">
                                        <SiReact className="text-blue-400 text-xl" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    rotate: 360
                                }}
                                transition={{ 
                                    duration: 30, 
                                    repeat: Infinity, 
                                    ease: "linear" 
                                }}
                            >
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
                                    <div className="w-14 h-14 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-green-500/20">
                                        <SiNodedotjs className="text-green-500 text-xl" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    rotate: 360
                                }}
                                transition={{ 
                                    duration: 30, 
                                    repeat: Infinity, 
                                    ease: "linear" 
                                }}
                            >
                                <div className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-14 h-14 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-green-400/20">
                                        <SiMongodb className="text-green-400 text-xl" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    rotate: 360
                                }}
                                transition={{ 
                                    duration: 30, 
                                    repeat: Infinity, 
                                    ease: "linear" 
                                }}
                            >
                                <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                                    <div className="w-14 h-14 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-blue-500/20">
                                        <SiTypescript className="text-blue-500 text-xl" />
                                    </div>
                                </div>
                            </motion.div>

                            {/* Floating icons - Appear and disappear in random positions */}
                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, 8, -6, 4, -8, 6, 0],
                                    y: [0, -6, 8, -4, 6, -8, 0]
                                }}
                                transition={{ 
                                    duration: 4,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            >
                                <div className="absolute top-1/4 left-1/4 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-12 h-12 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-yellow-400/20">
                                        <FaJs className="text-yellow-400 text-lg" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, -8, 6, -4, 8, -6, 0],
                                    y: [0, 6, -8, 4, -6, 8, 0]
                                }}
                                transition={{ 
                                    duration: 5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 0.5
                                }}
                            >
                                <div className="absolute bottom-1/4 right-1/4 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-12 h-12 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-cyan-400/20">
                                        <SiTailwindcss className="text-cyan-400 text-lg" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, 6, -8, 4, -6, 8, 0],
                                    y: [0, -8, 6, -4, 8, -6, 0]
                                }}
                                transition={{ 
                                    duration: 4.5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 1
                                }}
                            >
                                <div className="absolute top-1/3 left-1/3 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-12 h-12 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-gray-200/20">
                                        <SiNextdotjs className="text-gray-200 text-lg" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, -6, 8, -4, 6, -8, 0],
                                    y: [0, 8, -6, 4, -8, 6, 0]
                                }}
                                transition={{ 
                                    duration: 5.5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 1.5
                                }}
                            >
                                <div className="absolute bottom-1/3 right-1/3 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-12 h-12 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-gray-300/20">
                                        <SiExpress className="text-gray-300 text-lg" />
                                </div>
                            </div>
                            </motion.div>

                                                        <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, 1, -1, 0, -1, 1, 0],
                                    y: [0, -1, 1, 0, 1, -1, 0]
                                }}
                                transition={{ 
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 0
                                }}
                            >
                                <div className="absolute top-1/4 left-1/4 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-10 h-10 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-red-400/20">
                                        <FaGitAlt className="text-red-400 text-base" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, -1, 1, 0, 1, -1, 0],
                                    y: [0, 1, -1, 0, -1, 1, 0]
                                }}
                                transition={{ 
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 4
                                }}
                            >
                                <div className="absolute top-1/3 right-1/3 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-10 h-10 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-blue-400/20">
                                        <FaDocker className="text-blue-400 text-base" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, 1, -1, 0, -1, 1, 0],
                                    y: [0, -1, 1, 0, 1, -1, 0]
                                }}
                                transition={{ 
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 8
                                }}
                            >
                                <div className="absolute bottom-1/4 left-1/4 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-10 h-10 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-yellow-500/20">
                                        <FaAws className="text-yellow-500 text-base" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, -1, 1, 0, 1, -1, 0],
                                    y: [0, 1, -1, 0, -1, 1, 0]
                                }}
                                transition={{ 
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 12
                                }}
                            >
                                <div className="absolute bottom-1/3 left-1/3 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-10 h-10 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-yellow-500/20">
                                        <SiFirebase className="text-yellow-500 text-base" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, 4, -3, 2, -4, 3, 0],
                                    y: [0, -3, 4, -2, 3, -4, 0]
                                }}
                                transition={{ 
                                    duration: 4.4,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 4
                                }}
                            >
                                <div className="absolute top-1/4 right-1/4 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-8 h-8 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-red-500/20">
                                        <FaNpm className="text-red-500 text-sm" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, -4, 3, -2, 4, -3, 0],
                                    y: [0, 3, -4, 2, -3, 4, 0]
                                }}
                                transition={{ 
                                    duration: 5.4,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 4.5
                                }}
                            >
                                <div className="absolute bottom-1/4 left-1/4 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-8 h-8 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-pink-500/20">
                                        <SiGraphql className="text-pink-500 text-sm" />
                                </div>
                            </div>
                            </motion.div>

                                                        <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, 1, -1, 0, -1, 1, 0],
                                    y: [0, -1, 1, 0, 1, -1, 0]
                                }}
                                transition={{ 
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 16
                                }}
                            >
                                <div className="absolute top-1/2 left-1/6 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-8 h-8 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-gray-200/20">
                                        <SiSocketdotio className="text-gray-200 text-sm" />
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div 
                                className="absolute inset-0"
                                animate={{ 
                                    opacity: [0, 1, 0],
                                    scale: [0.8, 1, 0.8],
                                    x: [0, -1, 1, 0, 1, -1, 0],
                                    y: [0, 1, -1, 0, -1, 1, 0]
                                }}
                                transition={{ 
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 20
                                }}
                            >
                                <div className="absolute bottom-1/2 right-1/6 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-8 h-8 rounded-full glass flex items-center justify-center shadow-glow-sm hover:scale-110 transition-transform duration-300 border border-red-500/20">
                                        <SiRedis className="text-red-500 text-sm" />
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>

                <div
                    className="absolute left-1/2 transform -translate-x-1/2 animate-bounce-slow z-20 bottom-4 md:bottom-8"
                >
                    <button
                        onClick={scrollToAbout}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full glass flex items-center justify-center text-gray-400 hover:text-white hover:border-primary-500 hover:shadow-glow-sm transition-all duration-300"
                        aria-label="Scroll to About section"
                    >
                        <FaArrowDown className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </motion.section>
    );
};

export default Home;
